import { create } from 'zustand'

// === Font families ===

export type FontFamilyId = 'inter' | 'system' | 'plex-sans' | 'geist' | 'iosevka' | 'serif-eb'

export interface FontFamilyMeta {
  id: FontFamilyId
  label: string
  stack: string
  preview: string
  /** When true, falls back to web-safe stack — no @font-face needed. */
  systemSafe: boolean
}

/**
 * Font stacks. Web-fonts (Inter, IBM Plex Sans, Geist, EB Garamond) are loaded
 * via Google Fonts <link>; system fallbacks render immediately.
 */
export const FONT_FAMILIES: FontFamilyMeta[] = [
  {
    id: 'inter',
    label: 'Inter',
    stack: `Inter, "Helvetica Neue", Helvetica, "Liberation Sans", -apple-system, BlinkMacSystemFont, Arial, sans-serif`,
    preview: 'Aa Bb Cc',
    systemSafe: true,
  },
  {
    id: 'system',
    label: 'System Sans',
    stack: `-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif`,
    preview: 'Aa Bb Cc',
    systemSafe: true,
  },
  {
    id: 'plex-sans',
    label: 'IBM Plex Sans',
    stack: `"IBM Plex Sans", Inter, "Helvetica Neue", Arial, sans-serif`,
    preview: 'Aa Bb Cc',
    systemSafe: false,
  },
  {
    id: 'geist',
    label: 'Geist',
    stack: `Geist, Inter, -apple-system, BlinkMacSystemFont, sans-serif`,
    preview: 'Aa Bb Cc',
    systemSafe: false,
  },
  {
    id: 'iosevka',
    label: 'Iosevka (Mono-Look)',
    stack: `Iosevka, "JetBrains Mono", ui-monospace, Menlo, monospace`,
    preview: 'Aa Bb Cc',
    systemSafe: false,
  },
  {
    id: 'serif-eb',
    label: 'EB Garamond (Serif)',
    stack: `"EB Garamond", "Iowan Old Style", "Palatino Linotype", Palatino, serif`,
    preview: 'Aa Bb Cc',
    systemSafe: false,
  },
]

export const MONO_FONT_STACK = `"JetBrains Mono", ui-monospace, Menlo, "Cascadia Code", monospace`

// === Font scale ===

export type FontScaleId = 'compact' | 'default' | 'comfortable' | 'large'

export interface FontScaleMeta {
  id: FontScaleId
  label: string
  factor: number
  description: string
}

export const FONT_SCALES: FontScaleMeta[] = [
  { id: 'compact', label: 'Kompakt', factor: 0.92, description: 'Mehr Inhalt auf den Bildschirm' },
  { id: 'default', label: 'Standard', factor: 1.0, description: 'Empfohlene Größe' },
  {
    id: 'comfortable',
    label: 'Komfortabel',
    factor: 1.08,
    description: 'Etwas entspannter zu lesen',
  },
  { id: 'large', label: 'Groß', factor: 1.18, description: 'Maximale Lesbarkeit' },
]

// === Logo ===

export type LogoId = 'flame' | 'clock' | 'bolt' | 'feather' | 'mountain' | 'custom'

export interface LogoMeta {
  id: LogoId
  label: string
  /** Lucide icon name (matched in BrandLogo); ignored for 'custom'. */
  iconName: string | null
}

export const LOGO_PRESETS: LogoMeta[] = [
  { id: 'flame', label: 'Flame (Default)', iconName: 'Flame' },
  { id: 'clock', label: 'Clock', iconName: 'Clock' },
  { id: 'bolt', label: 'Bolt', iconName: 'Zap' },
  { id: 'feather', label: 'Feather', iconName: 'Feather' },
  { id: 'mountain', label: 'Mountain', iconName: 'Mountain' },
  { id: 'custom', label: 'Eigenes Logo', iconName: null },
]

// === Background ===

export type BackgroundId = 'glow' | 'plain' | 'noise' | 'mesh' | 'custom'

export interface BackgroundMeta {
  id: BackgroundId
  label: string
  description: string
}

export const BACKGROUNDS: BackgroundMeta[] = [
  {
    id: 'glow',
    label: 'Glow (Default)',
    description: 'Subtile Akzent-Glühränder oben + unten rechts',
  },
  { id: 'plain', label: 'Plain', description: 'Komplett ruhige Fläche, keine Gradients' },
  { id: 'noise', label: 'Noise', description: 'Sehr feines Korn — gibt der Fläche Tiefe' },
  { id: 'mesh', label: 'Mesh', description: 'Mehrere weiche Farb-Blobs (vibrant)' },
  { id: 'custom', label: 'Eigenes Bild', description: 'Hochgeladenes Hintergrundbild' },
]

// === Brand label ===

export const DEFAULT_BRAND_LABEL = 'Ember'

// === Persisted state ===

const STORAGE_KEY = 'embertime.appearance'

export interface AppearanceState {
  fontFamily: FontFamilyId
  fontScale: FontScaleId
  logo: LogoId
  /** dataURL of an uploaded logo (used when logo === 'custom'). */
  customLogoDataUrl: string | null
  brandLabel: string
  background: BackgroundId
  /** dataURL of an uploaded background image (used when background === 'custom'). */
  customBackgroundDataUrl: string | null
}

const DEFAULT_STATE: AppearanceState = {
  fontFamily: 'inter',
  fontScale: 'default',
  logo: 'flame',
  customLogoDataUrl: null,
  brandLabel: DEFAULT_BRAND_LABEL,
  background: 'glow',
  customBackgroundDataUrl: null,
}

function readStored(): AppearanceState {
  if (typeof window === 'undefined') return DEFAULT_STATE
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return DEFAULT_STATE
    const parsed = JSON.parse(raw) as Partial<AppearanceState>
    return {
      ...DEFAULT_STATE,
      ...parsed,
    }
  } catch {
    return DEFAULT_STATE
  }
}

function persist(state: AppearanceState) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // ignore quota / private-mode errors
  }
}

/**
 * Apply the appearance prefs to the document by setting CSS variables and
 * a data-attribute on the root element. The `<style>` rules in index.css
 * read these to switch fonts/scales/backgrounds at render time.
 */
export function applyToDocument(state: AppearanceState) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  const family = FONT_FAMILIES.find((f) => f.id === state.fontFamily) ?? FONT_FAMILIES[0]
  const scale = FONT_SCALES.find((s) => s.id === state.fontScale) ?? FONT_SCALES[1]
  root.style.setProperty('--font-sans', family.stack)
  root.style.setProperty('--font-mono', MONO_FONT_STACK)
  root.style.setProperty('--font-scale', String(scale.factor))
  root.dataset.bg = state.background
  if (state.background === 'custom' && state.customBackgroundDataUrl) {
    root.style.setProperty('--bg-custom-image', `url("${state.customBackgroundDataUrl}")`)
  } else {
    root.style.removeProperty('--bg-custom-image')
  }
}

interface AppearanceStore extends AppearanceState {
  setFontFamily: (id: FontFamilyId) => void
  setFontScale: (id: FontScaleId) => void
  setLogo: (id: LogoId) => void
  setCustomLogo: (dataUrl: string | null) => void
  setBrandLabel: (label: string) => void
  setBackground: (id: BackgroundId) => void
  setCustomBackground: (dataUrl: string | null) => void
  reset: () => void
}

export const useAppearance = create<AppearanceStore>((set, get) => {
  const initial = readStored()
  applyToDocument(initial)

  function update(patch: Partial<AppearanceState>) {
    const next = { ...get(), ...patch }
    applyToDocument(next)
    persist(next)
    set(patch)
  }

  return {
    ...initial,
    setFontFamily: (fontFamily) => update({ fontFamily }),
    setFontScale: (fontScale) => update({ fontScale }),
    setLogo: (logo) => update({ logo }),
    setCustomLogo: (customLogoDataUrl) => update({ customLogoDataUrl }),
    setBrandLabel: (brandLabel) => update({ brandLabel }),
    setBackground: (background) => update({ background }),
    setCustomBackground: (customBackgroundDataUrl) => update({ customBackgroundDataUrl }),
    reset: () => update(DEFAULT_STATE),
  }
})

export function bootAppearance(): void {
  applyToDocument(readStored())
}
